/**
 * Утилиты для нормализации данных
 * Устраняет дублирование функций нормализации во всем проекте
 */

/**
 * Нормализует ключи к camelCase
 * Первая буква маленькая, аббревиатуры — к нижнему регистру
 */
export function normalizeCamel(key: string): string {
  if (/^[A-Z0-9_]+$/.test(key) && key.length <= 4) return key.toLowerCase();
  return key.charAt(0).toLowerCase() + key.slice(1);
}

/**
 * Нормализует ключи к PascalCase
 */
export function normalizePascal(key: string): string {
  if (/^[a-z0-9_]+$/.test(key)) {
    return key.charAt(0).toUpperCase() + key.slice(1);
  }
  return key;
}

/**
 * Нормализует ответы пользователя к числовому формату
 */
export function normalizeAnswer(answerRaw: unknown): number | null {
  if (typeof answerRaw === 'number') {
    return answerRaw;
  }
  
  if (typeof answerRaw === 'string') {
    // Пробуем привести к числу
    const num = Number(answerRaw);
    if (!isNaN(num)) {
      // --- Likert 1–7 ---
      if (num >= 1 && num <= 7) {
        return (num - 4) / 3;
      } else {
        return num;
      }
    }
    
    // Специальные значения
    if (answerRaw === 'Да') return 1;
    if (answerRaw === 'Нет') return -1;
    if (answerRaw === 'Не помню' || answerRaw === 'Не помню / Не знаю') return 0;
  }
  
  return null;
}

/**
 * Ограничивает значение в диапазоне [-1, 1]
 */
export function clampToRange(value: number): number {
  if (value > 1) return 1;
  if (value < -1) return -1;
  return value;
}

/**
 * Нормализует объект, приводя все ключи к camelCase
 */
export function normalizeObjectKeys<T extends Record<string, any>>(obj: T): Record<string, T[keyof T]> {
  const normalized: Record<string, T[keyof T]> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    normalized[normalizeCamel(key)] = value;
  }
  
  return normalized;
}

/**
 * Создает scope с нормализованными ключами и значениями по умолчанию
 */
export function createNormalizedScope(
  scores: Record<string, number>,
  extendedMetrics: Record<string, any> = {},
  defaultVars: string[] = []
): Record<string, number> {
  const scope: Record<string, number> = {};
  
  // 1. Все переменные из scores (camelCase)
  for (const [key, value] of Object.entries(scores)) {
    const normalizedKey = normalizeCamel(key);
    scope[normalizedKey] = typeof value === 'number' && !isNaN(value) ? value : 0;
  }
  
  // 2. Все переменные из extendedMetrics
  for (const [key, value] of Object.entries(extendedMetrics)) {
    const normalizedKey = normalizeCamel(key);
    const numValue = typeof value === 'number' ? value : 
                    (typeof value === 'object' && value?.value ? value.value : 0);
    scope[normalizedKey] = typeof numValue === 'number' && !isNaN(numValue) ? numValue : 0;
  }
  
  // 3. Все переменные из defaultVars (если их нет — 0)
  for (const varName of defaultVars) {
    const normalizedKey = normalizeCamel(varName);
    if (!(normalizedKey in scope)) {
      scope[normalizedKey] = 0;
    }
  }
  
  return scope;
} 